import { useState, useEffect } from 'react'
import { 
  CurrencyDollarIcon,
  UserGroupIcon,
  ChartBarIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  DocumentChartBarIcon
} from '@heroicons/react/24/outline'
import api from '../../utils/api'

export default function InstructorReports() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dateRange, setDateRange] = useState('all') // all, month, week
  const [reports, setReports] = useState({
    overview: null,
    instructors: [],
    earnings: [],
    sessions: []
  })

  useEffect(() => {
    fetchReports()
  }, []) // Remove dateRange dependency to prevent reloading

  const fetchReports = async () => {
    try {
      setLoading(true)
      setError(null)
      const [overviewRes, instructorsRes, assignmentsRes] = await Promise.all([
        api.get('/admin/instructors/earnings/overview'),
        api.get('/admin/instructors'),
        api.get('/admin/instructors/assignments')
      ])

      // Calculate additional metrics
      const totalRevenue = assignmentsRes.data.reduce((sum, a) => sum + (a.compensation_amount || 0), 0)
      const completedSessions = assignmentsRes.data.reduce((sum, a) => sum + a.session_progress.completed, 0)
      const totalSessions = assignmentsRes.data.reduce((sum, a) => sum + a.session_progress.total, 0)

      setReports({
        overview: {
          ...overviewRes.data,
          total_revenue: totalRevenue,
          completed_sessions: completedSessions,
          total_sessions: totalSessions,
          completion_rate: totalSessions > 0 ? Math.round((completedSessions / totalSessions) * 100) : 0
        },
        instructors: instructorsRes.data,
        earnings: assignmentsRes.data,
        sessions: assignmentsRes.data
      })
    } catch (error) {
      console.error('Error fetching reports:', error)
      setError('Failed to load reports. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const exportToCSV = () => {
    const headers = ['Instructor', 'Student', 'Assignment Date', 'Status', 'Sessions Completed', 'Earnings', 'Payment Status']
    const rows = reports.earnings.map(a => [
      a.instructor.name,
      a.student.name,
      new Date(a.assigned_at).toLocaleDateString(),
      a.status,
      `${a.session_progress.completed}/${a.session_progress.total}`,
      `$${(parseFloat(a.earning_amount) || 0).toFixed(2)}`,
      a.earning_status
    ])

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `instructor-report-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchReports}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    )
  }

  if (!reports.overview) {
    return (
      <div className="text-center py-12 text-gray-500">
        No data available
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with Export */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Instructor Performance Reports</h2>
          <p className="text-sm text-gray-600 mt-1">Analytics and insights on instructor program</p>
        </div>
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          <DocumentChartBarIcon className="h-5 w-5" />
          Export CSV
        </button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <p className="text-blue-100">Total Revenue</p>
            <CurrencyDollarIcon className="h-8 w-8 text-blue-100" />
          </div>
          <p className="text-3xl font-bold">${(parseFloat(reports.overview.total_revenue) || 0).toFixed(2)}</p>
          <p className="text-sm text-blue-100 mt-2">
            {reports.overview.active_assignments} active assignments
          </p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <p className="text-green-100">Paid Out</p>
            <ArrowTrendingUpIcon className="h-8 w-8 text-green-100" />
          </div>
          <p className="text-3xl font-bold">${(parseFloat(reports.overview.total_paid) || 0).toFixed(2)}</p>
          <p className="text-sm text-green-100 mt-2">
            Pending: ${(reports.overview.total_pending || 0).toFixed(2)}
          </p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <p className="text-purple-100">Session Completion</p>
            <ChartBarIcon className="h-8 w-8 text-purple-100" />
          </div>
          <p className="text-3xl font-bold">{reports.overview.completion_rate}%</p>
          <p className="text-sm text-purple-100 mt-2">
            {reports.overview.completed_sessions}/{reports.overview.total_sessions} sessions
          </p>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <p className="text-orange-100">Active Instructors</p>
            <UserGroupIcon className="h-8 w-8 text-orange-100" />
          </div>
          <p className="text-3xl font-bold">{reports.overview.instructors_count}</p>
          <p className="text-sm text-orange-100 mt-2">
            Teaching {reports.overview.active_assignments} students
          </p>
        </div>
      </div>

      {/* Instructor Performance */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Instructor Performance</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Instructor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Students</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Earnings</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pending</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {reports.instructors.map((instructor) => (
                <tr key={instructor.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{instructor.name}</div>
                      <div className="text-sm text-gray-500">{instructor.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{instructor.active_students}</td>
                  <td className="px-6 py-4 text-sm font-medium text-green-600">
                    ${(parseFloat(instructor.total_earnings) || 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-yellow-600">
                    ${(parseFloat(instructor.pending_earnings) || 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4">
                    {instructor.active_students > 0 ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Available
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Session Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Top Performing Instructors</h3>
            <p className="text-sm text-gray-600">By session completion rate</p>
          </div>
          <div className="p-6">
            {reports.sessions
              .filter(a => a.session_progress.total > 0)
              .sort((a, b) => b.session_progress.percentage - a.session_progress.percentage)
              .slice(0, 5)
              .map((assignment, index) => (
                <div key={assignment.id} className="flex items-center justify-between mb-4 last:mb-0">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{assignment.instructor.name}</div>
                      <div className="text-sm text-gray-500">with {assignment.student.name}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">{assignment.session_progress.percentage}%</div>
                    <div className="text-xs text-gray-500">
                      {assignment.session_progress.completed}/{assignment.session_progress.total} sessions
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Payment Overview</h3>
            <p className="text-sm text-gray-600">Earnings by status</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Paid</span>
                  <span className="text-sm font-medium text-green-600">
                    ${(parseFloat(reports.overview.total_paid) || 0).toFixed(2)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-green-500 h-3 rounded-full"
                    style={{
                      width: `${(reports.overview.total_paid / (reports.overview.total_paid + reports.overview.total_pending)) * 100}%`
                    }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Pending</span>
                  <span className="text-sm font-medium text-yellow-600">
                    ${(reports.overview.total_pending || 0).toFixed(2)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-yellow-500 h-3 rounded-full"
                    style={{
                      width: `${(reports.overview.total_pending / (reports.overview.total_paid + reports.overview.total_pending)) * 100}%`
                    }}
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-900">Total</span>
                  <span className="text-lg font-bold text-gray-900">
                    ${((parseFloat(reports.overview.total_paid) || 0) + (parseFloat(reports.overview.total_pending) || 0)).toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {reports.overview.pending_count} payments pending
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Assignments</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Instructor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Earnings</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {reports.earnings
                .sort((a, b) => new Date(b.assigned_at) - new Date(a.assigned_at))
                .slice(0, 10)
                .map((assignment) => (
                  <tr key={assignment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(assignment.assigned_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{assignment.instructor.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{assignment.student.name}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${assignment.course_progress.percentage}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-600">{assignment.course_progress.percentage}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`font-medium ${
                        assignment.earning_status === 'paid' ? 'text-green-600' : 'text-yellow-600'
                      }`}>
                        ${(parseFloat(assignment.earning_amount) || 0).toFixed(2)}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
